// src/shared-components/forms/add-board-content/add-post/inside-header/index.jsx
import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import useGoBack from "@/hooks/useGoBack";
import useStateHandler from "@/hooks/useStateHandler";
import useTranslations from '@/i18n/useTranslations';
import GalleryService from "@/services/gallery.service";
import ContinueButton from "@/shared-components/buttons/components/ContinueButton";
import PublicationDatePopup from "@/shared-components/forms/add-board-content/add-article/inside-header/components/PublicationDatePopup";
import SaveAsDraftDialog from "@/shared-components/forms/add-board-content/add-article/inside-header/components/SaveAsDraftDialog";
import AudienceSelectionPopup from '@/shared-components/forms/add-board-content/shared-components/AudienceSelectionPopup';
import CalendarIcon from "@/shared-components/icons/components/forms/CalendarIcon";
import XIcon from '@/shared-components/icons/components/public/XIcon';
import Utils from '@/utils';
import { buildPublicContentPath } from '@/utils/contentRoutes';

import {
  applyPublicationAction,
  PUBLICATION_ACTIONS,
} from '../../publicationLifecycle';
import { getPostPayload, hasDraftablePostContent } from '../../saveFunctions';

import SavePostDraft from "./components/SavePostDraft";

import "@/shared-components/forms/add-board-content/add-article/inside-header/index.css";

export default function Header({
  formState,
  setFormState,
  formError,
  setFormError,
  editMode,
  onDraftDialogToggle,
  fetchData = {},
  membersAudienceEnabled = false
}) {
  const t = useTranslations('common.forms.board_editor');
  const { cacheHandler, stateHandler } = useStateHandler();
  const [openSaveAsDraft, setOpenSaveAsDraft] = useState(false);
  const [loading, setLoading] = useState(false);
  const [openPublicationDate, setOpenPublicationDate] = useState(false);
  const [openAudienceSelection, setOpenAudienceSelection] = useState(false);
  const goBackHandler = useGoBack("/profile/board");
  const initialState = useRef(formState);
  const hasInitializedDraftBaseline = useRef(false);

  // Establish the loaded-draft baseline once; do not reset it after every edit.
  useEffect(() => {
    if (
      editMode
      && formState._id
      && fetchData.post_status === 'incomplete'
      && !hasInitializedDraftBaseline.current
    ) {
      initialState.current = formState;
      hasInitializedDraftBaseline.current = true;
    }
  }, [editMode, fetchData.post_status, formState, formState._id]);
  
  const navigate = useNavigate();

  const hasContent = hasDraftablePostContent(formState);
  const hasScheduledPublication = Boolean(formState.publicationDate && formState.publicationTime);
  const isCalendarActive = hasScheduledPublication;

  // Whether we're editing an incomplete draft.
  const isDraftMode = editMode && fetchData.post_status === "incomplete";
  // same test: show inline button if new OR (editing AND incomplete)
  // 2) Only show the inline Save button when creating a new post (not editMode)
  //    OR when *editing* and it's still incomplete
  const showSave = !editMode || isDraftMode;

  useEffect(() => {
    onDraftDialogToggle?.(openSaveAsDraft);
  }, [openSaveAsDraft, onDraftDialogToggle]);

  async function savePost(onSaveCallback, {
    publicationAction = PUBLICATION_ACTIONS.PUBLISH,
    ...customFields
  } = {}) {
    if (!hasDraftablePostContent(formState)) {
      return { blocked: 'empty' };
    }

    setLoading(true);
    setFormError(null);

    if (Utils.isPostMediaUploading(formState)) { 
      setLoading(false);
      return { blocked: 'uploading' };
    }
    if (
      publicationAction === PUBLICATION_ACTIONS.PUBLISH
      && Utils.isPostVideoProcessing(formState)
    ) {
      setLoading(false);
      stateHandler.set('temporalPopup', {
        text: t('video_quality_processing'),
        variant: 'error',
      });
      return { blocked: 'video-processing' };
    }

    let payload = applyPublicationAction(
      getPostPayload(formState),
      publicationAction
    );

    payload = {
      ...payload,
      ...customFields,
    };

    const apiResponse = await GalleryService.submitPost(payload, true);
    setLoading(false);
    onSaveCallback?.(apiResponse);
    return apiResponse;
  }

  const onPublishCallback = (apiResponse) => {
    const { response, data } = apiResponse;
  
    if (response.ok) {
      cacheHandler.triggerAction("SUBMIT_POST");
      cacheHandler.storeInCache(`post_${data._id}_view`, data);
      navigate(buildPublicContentPath(data, 'post'), { replace: true, state: { from: true } });
      if (editMode) navigate(-1);
    } else if (response.status === 400) {
      setFormError({ element: data.error_data.element, error_code: data.error_type });
    } else if (data?.error_type === 'VIDEO_PROCESSING_INCOMPLETE') {
      stateHandler.set('temporalPopup', {
        text: t('video_quality_processing'),
        variant: 'error',
      });
    }
  };

  const onDraftCallback = (apiResponse) => {
    const { response, data } = apiResponse;
    if (response.ok) {
      cacheHandler.triggerAction("POST_DRAFT_SAVED");
      setFormState(prev => {
        const nextState = applyPublicationAction(
          { ...prev, _id: data._id },
          PUBLICATION_ACTIONS.SAVE_DRAFT
        );
        initialState.current = nextState;
        return nextState;
      });
    } else {
      alert('Could not save draft.');
    }
  };

  const handlePublish = () => {
    if (membersAudienceEnabled) {
      setOpenAudienceSelection(true);
      return;
    }

    savePost(onPublishCallback, { audience: 'public' });
  };

  const handleAudienceSelect = audience => {
    savePost(onPublishCallback, { audience });
  };

  return (
    <>
      <div className="board-header">
        <div className="board-header__left-options">
          <XIcon
            onClick={() => {
              if (
                JSON.stringify(initialState.current) !== JSON.stringify(formState)
                && hasContent
                && !editMode
              ) {
                setOpenSaveAsDraft(true);
              } else {
                goBackHandler();
              }
            }}
          />

          {/* only render on new OR incomplete‐draft */}
          {showSave && (
            <SavePostDraft
              formState={formState}
              hasContent={hasContent}
              initialStateRef={initialState}
              savePost={() => savePost(onDraftCallback, {
                publicationAction: PUBLICATION_ACTIONS.SAVE_DRAFT,
              })}
            />
          )}
        </div>

        <h1 className="board-header__heading">
          {editMode ? t('edit_post') : t('post')}
        </h1>

        <div className="board-header__right-options">
          <button
            type="button"
            className={`board-header__calendar-button${isCalendarActive ? ' board-header__calendar-button--active' : ''}`}
            onClick={() => setOpenPublicationDate(true)}
            aria-label={t('schedule_publication')}
            aria-pressed={isCalendarActive}
          >
            <CalendarIcon className="board-header__calendar-icon" />
          </button>
          {/* no preview for posts */}
          <ContinueButton
            turnOff={!hasContent}
            loading={loading}
            className="board-header__item publish element-non-selectable board-header__continue-small"
            onClick={handlePublish}
          >
            {t('publish')}
          </ContinueButton>
        </div>
      </div>

      {!editMode && (
        <SaveAsDraftDialog
          formState={formState}
          open={openSaveAsDraft}
          close={() => setOpenSaveAsDraft(false)}
          mode="post"
          onSaveDraft={() => savePost(onDraftCallback, {
            publicationAction: PUBLICATION_ACTIONS.SAVE_DRAFT,
          })}
        />
      )}

      <PublicationDatePopup
        open={openPublicationDate}
        onClose={() => setOpenPublicationDate(false)}
        formState={formState}
        setFormState={setFormState}
        formError={formError}
        setFormError={setFormError}
      />

      <AudienceSelectionPopup
        open={openAudienceSelection}
        onClose={() => setOpenAudienceSelection(false)}
        onSelect={handleAudienceSelect}
      />
    </>
  );
}
